import React from 'react';

/**
 * Firebase Error Boundary
 * Catches and handles Firebase-related errors gracefully
 */
class FirebaseErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      isNetworkError: false,
      isOnline: navigator.onLine
    };

    // Bind the event handlers
    this.handleOnline = this.handleOnline.bind(this);
    this.handleOffline = this.handleOffline.bind(this);
  }

  componentDidMount() {
    // Add online/offline event listeners
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  componentWillUnmount() {
    // Remove event listeners
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }

  handleOnline() {
    this.setState({ 
      isOnline: true,
      // If the error was a network error, reset it when coming back online
      hasError: this.state.isNetworkError ? false : this.state.hasError,
      isNetworkError: false
    });
  }

  handleOffline() {
    this.setState({ isOnline: false });
  }

  static getDerivedStateFromError(error) {
    // Check if it's a network error
    const isNetworkError = (
      error.name === 'NetworkError' || 
      error.message?.includes('network') || 
      error.message?.includes('offline') ||
      error.message?.includes('fetch') ||
      error.message?.includes('Failed to fetch') ||
      error.code === 'auth/network-request-failed' ||
      error.code === 'firestore/unavailable'
    );

    // Update state so the next render will show the fallback UI
    return { 
      hasError: true,
      isNetworkError
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error('🔥 Firebase Error Boundary caught an error:', error, errorInfo);
    
    // Check if it's likely a network error
    const isNetworkError = (
      error.name === 'NetworkError' || 
      error.message?.includes('network') || 
      error.message?.includes('offline') ||
      error.message?.includes('fetch') ||
      error.message?.includes('Failed to fetch') ||
      error.code === 'auth/network-request-failed' ||
      error.code === 'firestore/unavailable'
    );
    
    this.setState({
      error: error,
      errorInfo: errorInfo,
      isNetworkError
    });

    // Log Firebase-specific errors
    if (error.code && error.code.includes('auth/')) {
      console.error('🔐 Firebase Auth Error:', error.code, error.message);
    }
    
    if (error.code && error.code.includes('firestore/')) {
      console.error('🗄️ Firestore Error:', error.code, error.message);
    }

    // Handle specific address/coordinates error
    if (error.message && error.message.includes('deliveryAddress.coordinates')) {
      console.error('📍 Address Coordinates Error:', error.message);
      // Could try to fix the user profile here, but we'll handle in the UI
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  render() {
    // If we detected a network error or the browser reports being offline
    if (this.state.hasError && (this.state.isNetworkError || !this.state.isOnline)) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)',
          padding: '20px'
        }}>
          <div style={{
            maxWidth: '600px',
            background: 'white',
            borderRadius: '16px',
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>📶</div>
            <h2 style={{ color: '#e74c3c', marginBottom: '16px' }}>
              You're Offline
            </h2>
            <p style={{ color: '#666', marginBottom: '24px', lineHeight: '1.6' }}>
              It looks like you lost your internet connection. Please check your network settings and try again when you're back online.
            </p>
            
            <div style={{ 
              background: '#f8f9fa', 
              padding: '20px', 
              borderRadius: '8px', 
              marginBottom: '24px',
              textAlign: 'left'
            }}>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#666' }}>
                <li>Check your Wi-Fi connection</li>
                <li>Check your mobile data connection</li>
                <li>Try airplane mode on/off</li>
                <li>Try again in a few moments</li>
              </ul>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={this.handleRetry}
                style={{
                  background: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  transition: 'background 0.3s'
                }}
                onMouseOver={(e) => e.target.style.background = '#45a049'}
                onMouseOut={(e) => e.target.style.background = '#4CAF50'}
              >
                🔄 Retry Connection
              </button>
            </div>
            
            {/* This will automatically update when back online */}
            <div style={{ 
              marginTop: '24px', 
              padding: '16px', 
              background: '#ffe0b2', 
              borderRadius: '8px',
              fontSize: '14px',
              color: '#e65100'
            }}>
              <strong>ℹ️ Network Status:</strong><br/>
              {this.state.isOnline ? 'Browser reports online - retrying...' : 'Browser reports offline'}
            </div>
          </div>
        </div>
      );
    }
    
    // For other Firebase errors (not network related)
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--ff-primary-green)',
          padding: '20px'
        }}>
          <div style={{
            maxWidth: '600px',
            background: 'var(--ff-card-bg)',
            borderRadius: '16px',
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>🔥</div>
            <h2 style={{ color: '#e74c3c', marginBottom: '16px' }}>
              Firebase Configuration Error
            </h2>
            <p style={{ color: '#666', marginBottom: '24px', lineHeight: '1.6' }}>
              We're having trouble connecting to Firebase services. This could be due to:
            </p>
            
            <div style={{ 
              background: '#f8f9fa', 
              padding: '20px', 
              borderRadius: '8px', 
              marginBottom: '24px',
              textAlign: 'left'
            }}>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#666' }}>
                <li>API key restrictions or quota limits</li>
                <li>reCAPTCHA Enterprise configuration issues</li>
                <li>Network connectivity problems</li>
                <li>Firebase project settings</li>
              </ul>
            </div>

            {this.state.error && (
              <div style={{
                background: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px',
                textAlign: 'left'
              }}>
                <strong>Error Details:</strong>
                <pre style={{ 
                  fontSize: '12px', 
                  color: '#856404',
                  margin: '8px 0 0 0',
                  whiteSpace: 'pre-wrap'
                }}>
                  {typeof this.state.error.message === 'string' 
                    ? this.state.error.message 
                    : JSON.stringify(this.state.error.message, null, 2)}
                </pre>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={this.handleRetry}
                style={{
                  background: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  transition: 'background 0.3s'
                }}
                onMouseOver={(e) => e.target.style.background = '#45a049'}
                onMouseOut={(e) => e.target.style.background = '#4CAF50'}
              >
                🔄 Retry
              </button>
              
              <button
                onClick={() => window.location.href = '/'}
                style={{
                  background: '#2196F3',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  transition: 'background 0.3s'
                }}
                onMouseOver={(e) => e.target.style.background = '#1976D2'}
                onMouseOut={(e) => e.target.style.background = '#2196F3'}
              >
                🏠 Go Home
              </button>
            </div>

            <div style={{ 
              marginTop: '24px', 
              padding: '16px', 
              background: '#e3f2fd', 
              borderRadius: '8px',
              fontSize: '14px',
              color: '#1565c0'
            }}>
              <strong>🛠️ For Developers:</strong><br/>
              Check the browser console for detailed error information.<br/>
              See FIREBASE_TROUBLESHOOTING.md for common solutions.
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default FirebaseErrorBoundary;
